import { createSlice } from '@reduxjs/toolkit'

import _ from 'lodash'

import axios from 'axios'

import { reportingDashboardApi } from '../api/reporting-dashboard-api'
import slicesNotifications from './notifications'

const reportExtractState = {
    id: null,
    extract_id: null,
    subset_key: null,
    is_enabled: false,
    is_initiated: false,
    is_fetching: false,
    has_error: false,
    error_message: null,
    data: null,
    data_info: null,
    descriptors: []
}

const reportFilterState = {
    id: null,
    group_id: null,
    is_enabled: false,
    is_initiated: false,
    is_fetching: false,
    has_error: false,
    error_message: null,
    options: [],
    default_option_ids: [],
    selected_option_ids: [],
    restricted_to_extracts: [],
    descriptor: null,
    is_common_filter: false
}

const initialDashboardState = {
    id: null,
    is_initiated: false,
    is_fetching: false,
    has_changes: false,

    extracts_ids: [],
    extracts: [],

    parameters_ids: [],
    parameters: [],

    filters_ids: [],
    filters: [],
    filters_changelog: [],
    settings: {
        is_initiated: true,
        custom_headline_image: {
            enabled: true
        },
        custom_budget: {
            enabled: false,
            is_valid: false,
            has_custom_value: false,
            value: null
        },
        sections: {
            summary: true,
            delivery_performance: true,
            creative_performance: true,
            device_performance: true,
            appdomain_verification: true,
            geo_delivery: true
        }
    }
}

const initialState = {
    dashboards_ids: [],
    dashboards: []
}

const slice = createSlice({
    name: 'reporting_dashboards',
    initialState,
    reducers: {
        updateSettings(state, action) {
            const { dashboard_id, settings } = action.payload

            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            if (dashboardIndex == -1) {
                return state
            }

            state.dashboards[dashboardIndex]['settings'] = {
                ...state.dashboards[dashboardIndex]['settings'],
                ...settings
            }

            return state
        },

        initiate(state, action) {
            const { dashboard_id, extracts, parameters, filters } =
                action.payload

            const dashboard = _.cloneDeep(initialDashboardState)
            dashboard.id = dashboard_id

            //
            extracts.forEach((extract) => {
                const extract_state = _.cloneDeep(reportExtractState)
                extract_state.id = extract.id
                extract_state.extract_id = extract.extract_id
                extract_state.subset_key = extract.subset_key
                extract_state.is_enabled = true

                dashboard.extracts_ids.push(extract.id)
                dashboard.extracts.push(extract_state)
            })

            //
            parameters.forEach((parameter) => {
                dashboard.parameters_ids.push(parameter.db_field_name)
                dashboard.parameters.push({
                    id: parameter.db_field_name,
                    values: parameter.values
                })
            })

            //
            filters.forEach((filter) => {
                dashboard.filters_ids.push(filter.db_field_name)

                const filterState = _.cloneDeep(reportFilterState)
                filterState.id = filter.db_field_name
                filterState.group_id = filter.group_id
                filterState.is_common_filter = filter.is_common_filter
                filterState.options = []
                filterState.default_option_ids = [...filter.default_option_ids]
                filterState.selected_option_ids = [...filter.default_option_ids]
                filterState.restricted_to_extracts = [
                    ...filter.restricted_to_extracts
                ]

                dashboard.filters.push(filterState)
            })

            //
            dashboard.is_initiated = true
            dashboard.has_changes = true

            const index = state.dashboards_ids.indexOf(dashboard_id)

            if (index > -1) {
                state.dashboards[index] = {
                    ...dashboard
                }
            } else {
                state.dashboards_ids.push(dashboard_id)
                state.dashboards.push({ ...dashboard })
            }

            return state
        },

        setHasChanges(state, action) {
            const { dashboard_id } = action.payload

            //
            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            if (dashboardIndex == -1) {
                return state
            }

            state.dashboards[dashboardIndex] = {
                ...state.dashboards[dashboardIndex],
                has_changes: true
            }

            return state
        },

        updateFilterSelectedValues(state, action) {
            const {
                dashboard_id,
                db_field_name,
                selected_option_ids,
                restricted_to_extracts
            } = action.payload

            //
            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            if (dashboardIndex == -1) {
                return state
            }

            //
            const filters = state.dashboards[dashboardIndex]['filters'].map(
                (filter) => {
                    const match = db_field_name == filter.id

                    return match
                        ? {
                              ...filter,
                              selected_option_ids: [...selected_option_ids]
                          }
                        : { ...filter }
                }
            )

            //
            state.dashboards[dashboardIndex].has_changes = true
            state.dashboards[dashboardIndex]['filters'] = [...filters]
            state.dashboards[dashboardIndex]['filters_changelog'].push({
                db_field_name,
                selected_option_ids,
                restricted_to_extracts
            })

            return state
        },

        updateFiltersData(state, action) {
            const { dashboard_id, error_message, has_error, data } =
                action.payload

            //
            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            if (dashboardIndex == -1) {
                return state
            }

            //
            const filters = state.dashboards[dashboardIndex]['filters'].map(
                (filter) => {
                    const match = data.find(
                        (data_filter) => data_filter.id == filter.id
                    )

                    const options = [...match.options]

                    return match
                        ? {
                              ...filter,
                              is_initiated: true,
                              is_fetching: false,
                              options,
                              // selected_option_ids,
                              descriptor: { ...match.descriptor }
                          }
                        : { ...filter }
                }
            )

            //
            state.dashboards[dashboardIndex] = {
                ...state.dashboards[dashboardIndex],
                filters: [...filters]
            }

            return state
        },

        setAllExtractsAsFetching(state, action) {
            const { dashboard_id, filtered_extracts_ids } = action.payload

            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            //
            const extracts = state.dashboards[dashboardIndex]['extracts'].map(
                (extract) => {
                    const match =
                        !filtered_extracts_ids.length ||
                        filtered_extracts_ids.indexOf(extract.extract_id) != -1

                    return match
                        ? {
                              ...extract,
                              has_error: false,
                              error_message: null,
                              is_fetching: true
                          }
                        : {
                              ...extract
                          }
                }
            )

            //
            const filters = state.dashboards[dashboardIndex]['filters'].map(
                (filter) => {
                    let match = true
                    if (filtered_extracts_ids.length) {
                        if (
                            filter.is_common_filter &&
                            filtered_extracts_ids.indexOf('filters') != -1
                        ) {
                            match = true
                        } else {
                            match =
                                filter.restricted_to_extracts.filter(
                                    (restricted_to_extract) =>
                                        filtered_extracts_ids.indexOf(
                                            restricted_to_extract
                                        ) != -1
                                ).length > 0
                        }
                    }

                    return match
                        ? {
                              ...filter,
                              has_error: false,
                              error_message: null,
                              is_fetching: true
                          }
                        : {
                              ...filter
                          }
                }
            )

            //
            state.dashboards[dashboardIndex] = {
                ...state.dashboards[dashboardIndex],
                is_fetching: true,
                extracts: [...extracts],
                filters: [...filters]
            }

            return state
        },

        setDashboardDataFetchichAsFinished(state, action) {
            const { dashboard_id } = action.payload

            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            state.dashboards[dashboardIndex] = {
                ...state.dashboards[dashboardIndex],
                is_fetching: false,
                has_changes: false
            }

            return state
        },

        setExtractDataAsFailedToLoad(state, action) {
            const { dashboard_id, id, has_error, error_message } =
                action.payload

            //
            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            if (dashboardIndex == -1) {
                return state
            }

            //
            const extracts = state.dashboards[dashboardIndex]['extracts'].map(
                (extract) =>
                    extract.id == id
                        ? {
                              ...extract,
                              is_initiated: false,
                              is_fetching: false,
                              has_error: has_error,
                              error_message: error_message
                          }
                        : extract
            )

            //
            state.dashboards[dashboardIndex] = {
                ...state.dashboards[dashboardIndex],
                extracts: [...extracts]
            }

            return state
        },

        setFiltersDataAsFailedToLoad(state, action) {
            const { dashboard_id, has_error, error_message } = action.payload

            //
            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            if (dashboardIndex == -1) {
                return state
            }

            //
            const filters = state.dashboards[dashboardIndex]['filters'].map(
                (filter) => ({
                    ...filter,
                    is_initiated: false,
                    is_fetching: false,
                    has_error: has_error,
                    error_message: error_message
                })
            )

            //
            state.dashboards[dashboardIndex] = {
                ...state.dashboards[dashboardIndex],
                filters: [...filters]
            }

            return state
        },

        updateExtractData(state, action) {
            const {
                dashboard_id,
                id,
                data,
                data_info,
                has_error,
                error_message,
                descriptors
            } = action.payload

            //
            const dashboardIndex = state.dashboards_ids.indexOf(dashboard_id)

            if (dashboardIndex == -1) {
                return state
            }

            //
            const extracts = state.dashboards[dashboardIndex]['extracts'].map(
                (extract) =>
                    extract.id == id
                        ? {
                              ...extract,
                              is_initiated: true,
                              is_fetching: false,
                              has_error: has_error,
                              error_message: error_message,
                              data: data,
                              data_info: data_info,
                              descriptors: descriptors
                          }
                        : extract
            )

            //
            state.dashboards[dashboardIndex] = {
                ...state.dashboards[dashboardIndex],
                extracts: [...extracts]
            }

            return state
        }
    }
})

export const { reducer } = slice

export const initiate =
    (dashboard_id, extracts, parameters, filters) => async (dispatch) => {
        const payload = { dashboard_id, extracts, parameters, filters }
        dispatch(slice.actions.initiate(payload))
    }

export const updateFilter =
    (
        dashboard_id,
        db_field_name,
        selected_option_ids,
        restricted_to_extracts = []
    ) =>
    async (dispatch) => {
        const payload = {
            dashboard_id,
            db_field_name,
            selected_option_ids,
            restricted_to_extracts
        }
        dispatch(slice.actions.updateFilterSelectedValues(payload))
    }

export const updateSettings = (dashboard_id, settings) => async (dispatch) => {
    const payload = {
        dashboard_id,
        settings
    }
    dispatch(slice.actions.updateSettings(payload))
}

export const fetchData =
    (
        dashboard_id,
        extracts,
        filters,
        parameters,
        last_filter_changelog = null,
        overwrite_campaign_budget = null
    ) =>
    async (dispatch) => {
        const customCancelToken = axios.CancelToken.source()

        const handleApiResponse = (
            response,
            req_dashboard_id,
            req_id,
            req_extract_id,
            req_subset_key
        ) => {
            const { statusCode, errorCode, errorMessage } = response

            //
            if (statusCode == -1) {
                return response
            }

            if (statusCode !== 200) {
                const payload = {
                    dashboard_id: req_dashboard_id,
                    id: req_id,
                    extract_id: req_extract_id,
                    subset_key: req_subset_key,
                    error_message: errorMessage || 'Report Data Failed to load',
                    has_error: true
                }

                if (req_extract_id == 'filters') {
                    dispatch(
                        slice.actions.setFiltersDataAsFailedToLoad(payload)
                    )
                } else {
                    dispatch(
                        slice.actions.setExtractDataAsFailedToLoad(payload)
                    )
                }

                dispatch(
                    slicesNotifications.addApiError(
                        response,
                        'Report Data Failed to load'
                    )
                )
                return response
            }

            //
            const { dashboard_id, extract_id } = response.body.info

            //
            if (extract_id == 'filters') {
                const { error_message, has_error, data } = response.body.report
                const payload = { dashboard_id, error_message, has_error, data }
                dispatch(slice.actions.updateFiltersData(payload))
            } else {
                const {
                    descriptors,
                    error_message,
                    has_error,
                    data,
                    data_info
                } = response.body.report

                const payload = {
                    dashboard_id,
                    id: req_id,
                    descriptors,
                    error_message,
                    has_error,
                    data,
                    data_info
                }
                dispatch(slice.actions.updateExtractData(payload))
            }

            return response
        }

        const sendApiRequest = (id, extract_id, subset_key) => {
            const common_filters = filters
                .filter((filter) => filter.is_common_filter)
                .map((filter) => ({
                    id: filter.id,
                    restricted_to_extracts: [],
                    selected_option_ids: [...filter.selected_option_ids]
                }))

            const extract_filters = filters
                .filter(
                    (filter) =>
                        !filter.is_common_filter &&
                        (extract_id == 'filters' ||
                            filter.restricted_to_extracts.indexOf(extract_id) !=
                                -1)
                )
                .map((filter) => ({
                    id: filter.id,
                    restricted_to_extracts: filter.restricted_to_extracts,
                    selected_option_ids: [...filter.selected_option_ids]
                }))

            const axiosRequest = reportingDashboardApi.getExtractData(
                dashboard_id,
                id,
                extract_id,
                subset_key,
                common_filters,
                extract_filters,
                parameters,
                overwrite_campaign_budget,
                customCancelToken
            )

            return axiosRequest.then((response) =>
                handleApiResponse(
                    response,
                    dashboard_id,
                    id,
                    extract_id,
                    subset_key
                )
            )
        }

        const filtered_extracts = filteredExtracts(
            extracts,
            last_filter_changelog
        )

        const filtered_extracts_ids = [
            ...new Set(filtered_extracts.map((extract) => extract.extract_id))
        ]

        if (!filtered_extracts.length) {
            return false
        }

        dispatch(
            slice.actions.setAllExtractsAsFetching({
                dashboard_id,
                filtered_extracts_ids
            })
        )

        const api_extracts = filtered_extracts.map((extract) =>
            sendApiRequest(extract.id, extract.extract_id, extract.subset_key)
        )

        axios.all(api_extracts).then((results) => {
            const hasBeenCanceled =
                results.filter((result) => result.statusCode == -1).length > 0

            if (!hasBeenCanceled) {
                dispatch(
                    slice.actions.setDashboardDataFetchichAsFinished({
                        dashboard_id
                    })
                )
            }
        })
    }

const filteredExtracts = (extracts, last_filter_changelog) => {
    return extracts.filter(
        (extract) =>
            last_filter_changelog == null ||
            extract.extract_id === 'filters' ||
            !last_filter_changelog.restricted_to_extracts.length ||
            last_filter_changelog.restricted_to_extracts.indexOf(
                extract.extract_id
            ) !== -1
    )
}

export default {
    slice,
    initiate,
    updateFilter,
    fetchData,
    updateSettings,
    filteredExtracts
}
